// Lightweight IndexedDB wrapper using idb
// Assumes you'll store audio chunks with a composite key: `${assetId}|${signedUrl}`

import { openDB } from 'idb';

const DB_NAME = 'libretto-local-recording';
const STORE_NAME = 'chunks';
const DB_VERSION = 2;

export async function getChunkStore() {
    return openDB(DB_NAME, DB_VERSION, {
        upgrade(db) {
            if (!db.objectStoreNames.contains(STORE_NAME)) {
                db.createObjectStore(STORE_NAME);
            }
        },
    });
}

// Save a failed chunk
export async function saveFailedChunk(assetId, signedUrl, chunkData) {
    const db = await getChunkStore();
    const key = `${assetId}|${signedUrl}`;
    return db.put(STORE_NAME, { ...chunkData, assetId, signedUrl }, key);
}

// Get all failed chunks for a specific asset
export async function getFailedChunksForAsset(assetId) {
    const db = await getChunkStore();
    const keys = await db.getAllKeys();
    const assetKeys = keys.filter((key) => key.startsWith(`${assetId}|`));
    const entries = await Promise.all(
        assetKeys.map(async (key) => {
            const value = await db.get(STORE_NAME, key);
            return { key, ...value };
        })
    );
    return entries;
}

// Get all failed chunks (for retrying globally)
export async function getAllFailedChunks() {
    const db = await getChunkStore();
    const keys = await db.getAllKeys();
    const entries = await Promise.all(
        keys.map(async (key) => {
            const value = await db.get(STORE_NAME, key);
            return { key, ...value };
        })
    );
    return entries;
}

// Delete a chunk after successful retry
export async function deleteChunk(assetId, signedUrl) {
    const db = await getChunkStore();
    const key = `${assetId}|${signedUrl}`;
    return db.delete(STORE_NAME, key);
}

// Retry all failed uploads globally
export async function retryAllFailedChunks() {
    const failedChunks = await getAllFailedChunks();
    for (const { signedUrl, data, assetId } of failedChunks) {
        try {
            const res = await fetch(signedUrl, {
                method: 'PUT',
                body: data,
            });
            if (res.ok) {
                console.log(`Retry successful for chunk ${signedUrl}`);
                await deleteChunk(assetId, signedUrl);
            } else {
                console.warn(`Retry failed for chunk ${signedUrl}, status: ${res.status}`);
            }
        } catch (err) {
            console.error(`Retry failed for chunk ${signedUrl}`, err);
        }
    }
}

export async function retryFailedChunks(failedChunks) {
    for (const { signedUrl, data, assetId } of failedChunks) {
        try {
            const res = await fetch(signedUrl, {
                method: 'PUT',
                body: data,
            });
            if (res.ok) {
                console.log(`Retry successful for chunk ${signedUrl}`);
                await deleteChunk(assetId, signedUrl);
            } else {
                console.warn(`Retry failed for chunk ${signedUrl}, status: ${res.status}`);
            }
        } catch (err) {
            console.error(`Retry failed for chunk ${signedUrl}`, err);
        }
    }
}

// Retry all failed uploads for a given asset
export async function retryFailedChunksForAsset(assetId) {
    const failedChunks = await getFailedChunksForAsset(assetId);
    for (const { signedUrl, data } of failedChunks) {
        try {
            const res = await fetch(signedUrl, {
                method: 'PUT',
                body: data,
            });
            if (res.ok) {
                console.log(`Retry successful for chunk ${signedUrl}`);
                await deleteChunk(assetId, signedUrl);
            } else {
                console.warn(`Retry failed for chunk ${signedUrl}, status: ${res.status}`);
            }
        } catch (err) {
            console.error(`Retry failed for chunk ${signedUrl}`, err);
        }
    }
}